import { useApolloClient } from '@apollo/client'
import { SubmitHandler } from 'react-hook-form'
import { DIRECT_PAY_STARTED_EVENT } from '../../../../analytics/events'
import { useAnalytics } from '../../../../context/useAnalytics'
import {
    BillsDataQuery,
    SpritzCardQuery,
    useCreateDirectPaymentMutation,
} from '../../../../graphql/generated/graphql'
import { BILLS_QUERY } from '../../../../graphql/schema/liabilities'
import { CURRENT_USER_DATA_QUERY } from '../../../../graphql/schema/user'
import { useWeb3Connection } from '../../../../web3/Web3Provider'
import { useDirectPayContext } from './context/DirectPayContext'
import { useFundDirectPayment } from './useFundDirectPayment'
import { SPRITZ_CARD_QUERY } from '../../../../graphql/schema/card'

export interface CreateDirectPayFormData {
    amount: number
    rewardsAmount: number
    tokenAddress: string
    testPayment: boolean
}

export const usePayBill = () => {
    const { connectedNetwork } = useWeb3Connection()
    const { fundPayment, paying, actionLog, payState } = useFundDirectPayment()
    const { dispatch, selectedToken, account, type } = useDirectPayContext()
    const { trackAnalyticsEvent } = useAnalytics()
    const apolloClient = useApolloClient()

    const [createDirectPayment, { loading: creatingDirectPayment }] =
        useCreateDirectPaymentMutation({
            onCompleted: (data) =>
                dispatch({ type: 'set-direct-payment', payload: data.createDirectPayment }),
        })

    const payBill = async (formData: CreateDirectPayFormData, isDelayed = false) => {
        const amount = Number(formData.amount)
        const rewardsAmount = Number(formData.rewardsAmount ?? 0)
        const testPayment = formData.testPayment

        trackAnalyticsEvent(DIRECT_PAY_STARTED_EVENT, {
            amount,
            rewardsAmount,
            token: selectedToken?.symbol,
            network: connectedNetwork,
            type,
            testPayment,
        })
        const result = await createDirectPayment({
            variables: {
                createDirectPaymentInput: {
                    accountId: account.accountId,
                    amount,
                    rewardsAmount,
                    testPayment: formData.testPayment,
                    tokenAddress: formData.tokenAddress,
                    provider: account.provider,
                    network: connectedNetwork,
                },
            },
        })
        if (!result.data?.createDirectPayment) return

        const amountDue = result.data.createDirectPayment.amountDue

        const succeeded = await fundPayment(
            amountDue,
            result.data.createDirectPayment.id,
            new Date(result.data.createDirectPayment.currentTime)
        )

        if (!succeeded) return

        if (rewardsAmount) {
            apolloClient.cache.updateQuery(
                {
                    query: CURRENT_USER_DATA_QUERY,
                },
                (data) => {
                    return {
                        ...data,
                        rewardsBalance: data.rewardsBalance - rewardsAmount,
                    }
                }
            )
        }

        setTimeout(() => {
            apolloClient.refetchQueries({
                include: ['PaymentsInProgress', 'Payments'],
            })
        }, 2000)

        if (type === 'spritzCard' && !isDelayed) {
            apolloClient.cache.updateQuery<SpritzCardQuery>(
                {
                    query: SPRITZ_CARD_QUERY,
                },
                //@ts-ignore
                (data) => {
                    const previousBalance = data?.virtualCard?.balance ?? 0

                    //@ts-ignore
                    return {
                        virtualCard: {
                            ...(data?.virtualCard ?? {}),
                            balance: previousBalance + amount + rewardsAmount,
                        },
                    }
                }
            )
        }

        if (type === 'billPay') {
            apolloClient.cache.updateQuery<BillsDataQuery>(
                {
                    query: BILLS_QUERY,
                },
                //@ts-ignore
                (data) => ({
                    ...data,
                    //@ts-ignore
                    bills: (data?.bills ?? []).map((bill) => ({
                        ...bill,
                        testPaymentAvailable:
                            account.accountId === bill.id ? false : bill.testPaymentAvailable,
                    })),
                })
            )
        }
    }

    return {
        payBill,
        paying: creatingDirectPayment || paying,
        actionLog,
        payState,
    }
}
